import { Container, Graphics } from "pixi.js";
import { COLORS, DEFAULT_PITCH_SKIN, FIELD, pitchSkin, TEAM_IDENTITY, type Ball, type PitchSkinId, type Player } from "@golazo/shared";
import { drawFutsalRadarMarks, isFutsalSkin } from "./futsalLines.js";
import { isMobileHandheld } from "../mobile/detect.js";

export interface RadarRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface RadarLayer {
  container: Container;
  setVisible(on: boolean): void;
  /**
   * `view` es el trozo de campo que se esta viendo. Si cubre el campo entero
   * no se dibuja nada: en los modos sin acercamiento el marco seria un borde
   * pegado al marco del radar, puro ruido.
   */
  sync(players: readonly Player[], ball: Ball, humanId: string, view?: RadarRect | null): void;
  layout(screenWidth: number, screenHeight: number): void;
}

const RADAR_W = 168;
const RADAR_H = 105;
const PAD = 14;

/** Escala del minimapa en tactil, donde el espacio vertical es más escaso. */
const TOUCH_SCALE = 0.62;

/**
 * Minimapa. Solo lectura espacial: no es asistencia de apuntado, es mapa.
 * Imprescindible en 5v5/11v11; en 3v3 tambien orienta.
 */
export function createRadarLayer(skinId: PitchSkinId = DEFAULT_PITCH_SKIN): RadarLayer {
  const skin = pitchSkin(skinId);
  const container = new Container();
  container.label = "radar";

  const frame = new Graphics();
  container.addChild(frame);

  const pitch = new Graphics();
  container.addChild(pitch);

  // Debajo de los puntos: el marco de camara es contexto, no informacion.
  const viewBox = new Graphics();
  container.addChild(viewBox);

  const dots = new Graphics();
  container.addChild(dots);

  let visible = true;
  const lineColor = skin.line || COLORS.LINE;

  const drawChrome = (): void => {
    frame.clear();
    frame
      .roundRect(0, 0, RADAR_W, RADAR_H, 8)
      .fill({ color: 0x0d160f, alpha: 0.72 })
      .stroke({ width: 1.5, color: COLORS.HUD_TEXT, alpha: 0.22 });

    pitch.clear();
    const ix = 8;
    const iy = 8;
    const iw = RADAR_W - 16;
    const ih = RADAR_H - 16;
    pitch.roundRect(ix, iy, iw, ih, 4).fill({ color: skin.stripeB, alpha: 0.72 });
    if (isFutsalSkin(skin)) {
      drawFutsalRadarMarks(pitch, ix, iy, iw, ih, lineColor);
    } else {
      pitch
        .moveTo(ix + iw / 2, iy)
        .lineTo(ix + iw / 2, iy + ih)
        .stroke({ width: 1, color: lineColor, alpha: 0.35 });
      pitch.circle(ix + iw / 2, iy + ih / 2, Math.min(iw, ih) * 0.12).stroke({
        width: 1,
        color: lineColor,
        alpha: 0.3,
      });
      const boxW = iw * 0.14;
      const boxH = ih * 0.45;
      pitch
        .rect(ix, iy + (ih - boxH) / 2, boxW, boxH)
        .stroke({ width: 1, color: lineColor, alpha: 0.28 });
      pitch
        .rect(ix + iw - boxW, iy + (ih - boxH) / 2, boxW, boxH)
        .stroke({ width: 1, color: lineColor, alpha: 0.28 });
    }
  };

  drawChrome();

  return {
    container,

    setVisible(on: boolean): void {
      visible = on;
      container.visible = on;
    },

    sync(players, ball, humanId, view = null): void {
      if (!visible) return;
      const ix = 8;
      const iy = 8;
      const iw = RADAR_W - 16;
      const ih = RADAR_H - 16;

      const toX = (x: number): number => ix + (x / FIELD.WIDTH) * iw;
      const toY = (y: number): number => iy + (y / FIELD.HEIGHT) * ih;

      viewBox.clear();
      if (view && (view.width < FIELD.WIDTH * 0.98 || view.height < FIELD.HEIGHT * 0.98)) {
        const x0 = Math.max(ix, toX(view.x));
        const y0 = Math.max(iy, toY(view.y));
        const x1 = Math.min(ix + iw, toX(view.x + view.width));
        const y1 = Math.min(iy + ih, toY(view.y + view.height));
        viewBox
          .rect(x0, y0, x1 - x0, y1 - y0)
          .fill({ color: COLORS.HUD_TEXT, alpha: 0.07 })
          .stroke({ width: 1, color: COLORS.HUD_TEXT, alpha: 0.4 });
      }

      dots.clear();
      for (const p of players) {
        const color = TEAM_IDENTITY[p.side].kit.primary;
        const r = p.id === humanId ? 3.4 : p.role === "gk" ? 2.2 : 2.6;
        dots.circle(toX(p.pos.x), toY(p.pos.y), r).fill({ color, alpha: p.id === humanId ? 1 : 0.85 });
        if (p.id === humanId) {
          dots.circle(toX(p.pos.x), toY(p.pos.y), r + 2.5).stroke({
            width: 1.2,
            color: COLORS.CONTROL_RING,
            alpha: 0.9,
          });
        }
      }
      dots.circle(toX(ball.pos.x), toY(ball.pos.y), 2.2).fill({ color: COLORS.BALL });
    },

    layout(screenWidth: number, screenHeight: number): void {
      const mobile = isMobileHandheld() || (screenWidth < 920 && screenHeight < 520);
      /*
       * El minimapa ocupa el centro superior en todos los dispositivos. Es la
       * información que más se consulta y aquí queda fuera de las manos y de
       * la acción principal. En tactil se encoge para no comerse la franja
       * central del campo.
       */
      const scale = mobile ? TOUCH_SCALE : 1;
      container.scale.set(scale);
      container.x = Math.round((screenWidth - RADAR_W * scale) / 2);
      container.y = PAD;
      /*
       * Translucido, porque ahora flota SOBRE el cesped.
       *
       * Antes el campo empezaba por debajo del minimapa —se le reservaba una
       * franja— y podia ser opaco sin tapar nada. Al dejar que el cesped ocupe
       * la ventana entera esa franja desaparece, y un rectangulo opaco en el
       * centro de arriba escondería el saque de banda. Al 78% se sigue leyendo
       * de un vistazo y se adivina lo que pasa debajo.
       */
      container.alpha = 0.78;
    },
  };
}
